
import { useEffect, useCallback } from 'react';
import { UserCategory } from '@/types';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';

interface UseCategoryEventsOptions {
  categories: UserCategory[];
  pendingOperationsCount: number;
  loadCategories: () => Promise<void>;
  updatePendingCount: () => Promise<void>;
  syncOnlyCategories: () => Promise<void>;
  setCategories: (categories: UserCategory[]) => void;
}

export const useCategoryEvents = ({
  categories,
  pendingOperationsCount,
  loadCategories,
  updatePendingCount,
  syncOnlyCategories,
  setCategories
}: UseCategoryEventsOptions) => {

  // Listener per cambi di stato rete
  useEffect(() => {
    const handleNetworkChange = async (status: { connected: boolean }) => {
      if (status.connected && pendingOperationsCount > 0) {
        OfflineLogger.info('useCategoryEvents', 'Network reconnected, starting category-specific sync');
        await syncOnlyCategories();
      }
    };

    const unsubscribe = networkService.addListener(handleNetworkChange);
    return () => unsubscribe();
  }, [pendingOperationsCount, syncOnlyCategories]);

  // ✅ OTTIMIZZAZIONE: Listener modificato per gestire solo sync remoti, non operazioni locali
  useEffect(() => {
    const handleCategoriesSyncCompleted = async (event: CustomEvent) => {
      OfflineLogger.info('useCategoryEvents', `Categories sync completed event received: ${JSON.stringify(event.detail)}`);
      
      // ✅ FIX: Gestisci solo eventi di sincronizzazione remota, non operazioni locali
      const eventSource = event.detail?.source;
      
      // Ignora eventi generati da operazioni locali per evitare refresh
      if (eventSource === 'local-create' || eventSource === 'local-update' || eventSource === 'local-delete') {
        await updatePendingCount();
        return;
      }
      
      // Gestisci solo eventi di sincronizzazione remota o senza source specificato
      if (event.detail?.categories && Array.isArray(event.detail.categories) && event.detail.categories.length > 0) {
        setCategories(event.detail.categories);
      } else {
        // Solo ricarica se siamo online o se non abbiamo categorie locali
        const isOnline = networkService.isOnline();
        if (isOnline || categories.length === 0) {
          await loadCategories();
        }
      }
      
      await updatePendingCount();
    };

    window.addEventListener('categoriesSyncCompleted', handleCategoriesSyncCompleted as EventListener);
    return () => {
      window.removeEventListener('categoriesSyncCompleted', handleCategoriesSyncCompleted as EventListener);
    };
  }, [loadCategories, updatePendingCount, categories.length, setCategories]);
};
